import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wallet, MapPin, TrendingUp, Landmark, Compass } from "lucide-react";
import { useHomeIQ } from "@/homeiq/state/HomeIQContext";
import GenerateReportModal from "@/components/persistent/GenerateReportModal";

const EASE = [0.16, 1, 0.3, 1];

// Single source of truth for pillar reasons — reads each tier's live output
// and produces one explanatory sentence. No new scoring, no charts.
function buildPillarReasons({ affordabilityOutputs, loanOutputs, marketOutputs, investmentOutputs }) {
    const reasons = {};

    // Affordability
    {
        const segGood = affordabilityOutputs.segment && affordabilityOutputs.segment !== "Entry";
        const dpRatio = affordabilityOutputs.eligible_loan > 0
            ? (affordabilityOutputs.eligible_loan && (1 - affordabilityOutputs.eligible_loan / (affordabilityOutputs.eligible_loan + 1))) // placeholder
            : 0;
        const positives = [];
        const constraints = [];
        if (affordabilityOutputs.foir_status === "Acceptable") positives.push(`FOIR is within bank rule`);
        else constraints.push(`FOIR usage at ${affordabilityOutputs.foir_usage_pct.toFixed(0)}% is above the rule`);
        if (segGood) positives.push(`${affordabilityOutputs.segment} segment unlocked`);
        if (affordabilityOutputs.is_affordability_alert) constraints.push(`affordability alert active (FOIR / DP / obligations)`);
        reasons.affordability = {
            positive: positives[0] ?? `Eligible loan ~₹${Math.round(affordabilityOutputs.eligible_loan / 100000)} L`,
            constraint: constraints[0] ?? `Headroom on DP ratio or segment could push score higher`,
        };
    }

    // Loan
    {
        const lo = loanOutputs;
        const interestPctOfOutflow = lo.interest_pct;
        const emiVsSalary = lo.emi_to_salary_pct ?? 0;
        const positives = [];
        const constraints = [];
        if (lo.rate <= 8.5) positives.push(`rate at ${lo.rate}% is at/below mainstream benchmark`);
        else constraints.push(`rate at ${lo.rate}% leaves room to negotiate`);
        if (emiVsSalary < 35) positives.push(`EMI under 35% of salary`);
        else constraints.push(`EMI is ${emiVsSalary.toFixed(0)}% of salary — limited headroom`);
        if (interestPctOfOutflow < 50) positives.push(`principal share ${100 - interestPctOfOutflow}% of total outflow`);
        else constraints.push(`interest eats ${interestPctOfOutflow}% of total outflow`);
        reasons.loan = {
            positive: positives[0] ?? `Tenure ${lo.tenure} yrs keeps total interest in check`,
            constraint: constraints[0] ?? `Shorter tenure or rate cut would help`,
        };
    }

    // Market
    {
        const mo = marketOutputs;
        const positives = [];
        const constraints = [];
        if (mo.within_count >= 5) positives.push(`${mo.within_count} markets fall within your budget`);
        else if (mo.within_count >= 1) constraints.push(`only ${mo.within_count} ${mo.within_count === 1 ? "market is" : "markets are"} within budget`);
        else constraints.push(`no markets are within budget — affordability needs a lift`);
        if (mo.best_within && mo.best_within.growth >= 7) positives.push(`${mo.best_within.area} shows ${mo.best_within.growth}% CAGR`);
        if (mo.beyond_count > mo.within_count) constraints.push(`${mo.beyond_count} markets sit beyond your budget`);
        reasons.market = {
            positive: positives[0] ?? `Reachable share ${mo.reachable_pct.toFixed(0)}%`,
            constraint: constraints[0] ?? `Stretching budget by 10% unlocks more options`,
        };
    }

    // Investment
    {
        if (investmentOutputs.blocked) {
            reasons.investment = {
                positive: "Rent not yet entered",
                constraint: "Enter monthly rent to unlock the investment pillar",
            };
        } else {
            const r = investmentOutputs.recommendation;
            const positives = [];
            const constraints = [];
            if (r.financial.verdict === "Buy") positives.push(`buyer ${r.financial.marginPct.toFixed(0)}% ahead of renter-investor at horizon`);
            else if (r.financial.verdict === "Rent") constraints.push(`renter ${Math.abs(r.financial.marginPct).toFixed(0)}% ahead at horizon`);
            if (r.market.verdict === "Strong") positives.push(`${r.market.area} is strong market opportunity`);
            else if (r.market.verdict === "Weak") constraints.push(`${r.market.area ?? "Selected market"} CAGR is muted`);
            if (r.lifestyle.verdict === "Comfortable") positives.push(`monthly cashflow is comfortable`);
            else if (r.lifestyle.verdict === "Tight") constraints.push(`lifestyle is tight — FOIR / DP / cashflow under pressure`);
            else if (r.lifestyle.verdict === "Stretch") constraints.push(`lifestyle is a stretch — one of FOIR / DP / cashflow is high`);
            reasons.investment = {
                positive: positives[0] ?? `Break-even at year ${investmentOutputs.buyVsRentBreakEvenYear ?? "—"}`,
                constraint: constraints[0] ?? `Tax benefits not modelled yet — adding them lifts buyer math`,
            };
        }
    }

    return reasons;
}

// ── Why? Drawer ─────────────────────────────────────────────────────
function WhyDrawer({ open, onClose }) {
    const { homeIQScore, affordabilityOutputs, loanOutputs, marketOutputs, investmentOutputs, setActiveSection } = useHomeIQ();

    // Lock body scroll while open.
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
        };
    }, [open, onClose]);

    const pillars = homeIQScore.pillars;
    const includedPillars = pillars.filter((p) => p.included);
    const reasons = buildPillarReasons({ affordabilityOutputs, loanOutputs, marketOutputs, investmentOutputs });

    // Biggest positive driver: highest score (tie-broken by higher contribution).
    // Biggest constraint: lowest score (tie-broken by lower contribution).
    let driver = null;
    let constraint = null;
    if (includedPillars.length > 0) {
        const sorted = [...includedPillars].sort((a, b) => b.score - a.score || b.contribution - a.contribution);
        driver = sorted[0];
        constraint = sorted[sorted.length - 1];
        // If a pillar is both driver and constraint (only 1 included), don't duplicate.
        if (sorted.length === 1) constraint = null;
    }

    const tone = (s) => s >= 75 ? "#16a34a" : s >= 55 ? "#d97706" : "#dc2626";
    const compositeTone = tone(homeIQScore.score);

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25, ease: EASE }}
                        onClick={onClose}
                        data-testid="why-drawer-backdrop"
                        className="fixed inset-0 z-40 bg-[#0A1F44]/40 backdrop-blur-sm"
                    />
                    <motion.aside
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ duration: 0.45, ease: EASE }}
                        data-testid="why-drawer"
                        role="dialog"
                        aria-label="HomeIQ score breakdown"
                        className="fixed top-0 right-0 z-50 h-full w-full sm:w-[420px] bg-white shadow-2xl overflow-y-auto"
                    >
                        {/* Header */}
                        <div className="sticky top-0 bg-white border-b border-[#0A1F44]/8 px-5 py-4 flex items-center justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-[#1E6BFF] font-semibold">— Why this score?</div>
                                <div className="font-display font-extrabold text-lg text-[#0A1F44]">HomeIQ breakdown</div>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                data-testid="why-drawer-close"
                                aria-label="Close breakdown drawer"
                                className="w-9 h-9 rounded-full border border-[#0A1F44]/10 flex items-center justify-center text-[#0A1F44]/65 hover:bg-[#0A1F44]/5 transition-colors"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Composite score */}
                        <section data-testid="why-composite" className="px-5 py-5 border-b border-[#0A1F44]/8">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">Composite score</div>
                            <div className="mt-2 flex items-baseline gap-3">
                                <div
                                    className="font-display font-black text-5xl tabular-nums"
                                    style={{ color: compositeTone }}
                                >
                                    {homeIQScore.score}
                                </div>
                                <div className="text-sm font-semibold" style={{ color: compositeTone }}>
                                    {homeIQScore.verdict}
                                </div>
                            </div>
                            <p className="mt-2 text-[11px] text-[#0A1F44]/60 leading-relaxed">
                                Weighted average across {includedPillars.length} active pillar{includedPillars.length === 1 ? "" : "s"}. Weights re-normalise when a pillar is missing inputs.
                            </p>
                        </section>

                        {/* Pillar breakdown */}
                        <section data-testid="why-pillar-breakdown" className="px-5 py-5 border-b border-[#0A1F44]/8">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold mb-3">Pillar contribution</div>
                            <div className="space-y-2">
                                {pillars.map((p) => {
                                    const isMissing = !p.included;
                                    const t = isMissing ? "#0A1F44" : tone(p.score);
                                    return (
                                        <div
                                            key={p.id}
                                            data-testid={`why-pillar-${p.id}`}
                                            className="flex items-center justify-between gap-3 py-1.5"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold text-[#0A1F44]">{p.label}</div>
                                                <div className="text-[10px] text-[#0A1F44]/55">
                                                    Weight {(p.weight * 100).toFixed(0)}%
                                                    {isMissing
                                                        ? " · not included"
                                                        : ` · effective ${(p.effectiveWeight * 100).toFixed(0)}%`}
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="font-display font-extrabold text-base tabular-nums" style={{ color: t }}>
                                                    {isMissing ? "—" : p.score}
                                                </div>
                                                <div className="text-[10px] text-[#0A1F44]/55 tabular-nums">
                                                    {isMissing ? "—" : `+${p.contribution.toFixed(1)} pts`}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>

                        {/* Driver + Constraint */}
                        {driver && (
                            <section data-testid="why-driver" className="px-5 py-5 border-b border-[#0A1F44]/8">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-[#16a34a] font-semibold mb-2">Biggest positive driver</div>
                                <div className="rounded-2xl bg-[#16a34a]/8 border border-[#16a34a]/20 p-4">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <div className="font-display font-extrabold text-base text-[#0A1F44]">{driver.label}</div>
                                        <div className="font-display font-extrabold text-lg text-[#16a34a] tabular-nums">{driver.score}</div>
                                    </div>
                                    <p className="mt-1 text-xs text-[#0A1F44]/70 leading-relaxed">
                                        {reasons[driver.id]?.positive}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => { setActiveSection(driver.id); onClose(); }}
                                        data-testid={`why-driver-cta-${driver.id}`}
                                        className="mt-2 text-[11px] font-semibold text-[#1E6BFF] hover:text-[#0A1F44] underline-offset-2 hover:underline"
                                    >
                                        Open {driver.label} →
                                    </button>
                                </div>
                            </section>
                        )}

                        {constraint && (
                            <section data-testid="why-constraint" className="px-5 py-5 border-b border-[#0A1F44]/8">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-[#dc2626] font-semibold mb-2">Biggest constraint</div>
                                <div className="rounded-2xl bg-[#dc2626]/8 border border-[#dc2626]/20 p-4">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <div className="font-display font-extrabold text-base text-[#0A1F44]">{constraint.label}</div>
                                        <div className="font-display font-extrabold text-lg text-[#dc2626] tabular-nums">{constraint.score}</div>
                                    </div>
                                    <p className="mt-1 text-xs text-[#0A1F44]/70 leading-relaxed">
                                        {reasons[constraint.id]?.constraint}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => { setActiveSection(constraint.id); onClose(); }}
                                        data-testid={`why-constraint-cta-${constraint.id}`}
                                        className="mt-2 text-[11px] font-semibold text-[#1E6BFF] hover:text-[#0A1F44] underline-offset-2 hover:underline"
                                    >
                                        Address {constraint.label} →
                                    </button>
                                </div>
                            </section>
                        )}

                        {/* Footer note */}
                        <section className="px-5 py-4">
                            <p data-testid="why-vastu-note" className="text-[10px] text-[#0A1F44]/50 italic leading-snug">
                                Vastu pillar will join the composite once Tier 6 lands — weights rebalance automatically.
                            </p>
                        </section>
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
}

// Phase 1 placeholder thresholds — same logic as the composite ring.
function scoreTone(v) {
    if (typeof v !== "number") return "#0A1F44/55";
    if (v >= 75) return "#16a34a";
    if (v >= 55) return "#d97706";
    return "#dc2626";
}

export default function DecisionDashboard() {
    const { activeSection, setActiveSection, homeIQScore } = useHomeIQ();
    const [whyOpen, setWhyOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [compact, setCompact] = useState(false);
    const score = homeIQScore.score;
    const tone =
        score >= 75 ? "#16a34a" : score >= 55 ? "#d97706" : "#dc2626";
    const verdict = homeIQScore.verdict;

    // On mobile the dashboard is heavy — full score ring + pills eats a
    // fifth of the viewport. Collapse to a slim strip once the user has
    // scrolled past the intro and expand back near the top of the page.
    // Desktop keeps the full layout at all times.
    useEffect(() => {
        const onScroll = () => setCompact(window.scrollY > 180);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const pillarById = homeIQScore.pillars.reduce((acc, p) => {
        acc[p.id] = p;
        return acc;
    }, {});
    const pills = [
        { id: "affordability", label: "Affordability", short: "Aff",  Icon: Wallet,      value: pillarById.affordability?.score ?? "—" },
        { id: "market",        label: "Market",        short: "Mkt",  Icon: MapPin,      value: pillarById.market?.score ?? "—" },
        { id: "investment",    label: "Investment",    short: "Inv",  Icon: TrendingUp,  value: pillarById.investment?.score ?? "—" },
        { id: "loan",          label: "Loan",          short: "Loan", Icon: Landmark,    value: pillarById.loan?.score ?? "—" },
        { id: "vastu",         label: "Vastu",         short: "Vst",  Icon: Compass,     value: "—" },
    ];

    // The Generate Report button on desktop lives inline in the header
    // row; on mobile it moves to a fixed bottom action bar so it is
    // always one thumb-tap away from any module tab.
    const openReport = () => setReportOpen(true);

    return (
        <>
            <div
                data-testid="decision-dashboard"
                data-compact={compact ? "true" : "false"}
                className="sticky top-14 md:top-16 z-30 bg-white/90 backdrop-blur-xl border-b border-[#0A1F44]/8"
            >
                {/* Compact mobile strip — appears once the user has scrolled
                    past the intro. Tap to open the Why? drawer. */}
                <div
                    data-testid="dashboard-compact-strip"
                    className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-200 ease-out ${
                        compact ? "max-h-14 opacity-100" : "max-h-0 opacity-0"
                    }`}
                >
                    <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
                        <button
                            type="button"
                            onClick={() => setWhyOpen(true)}
                            data-testid="dashboard-compact-score"
                            aria-label={`HomeIQ score ${score}, ${verdict}. Tap to open breakdown.`}
                            className="flex items-center gap-2 min-w-0"
                        >
                            <span className="text-[9px] tracking-[0.25em] uppercase text-[#0A1F44]/55 font-semibold">HomeIQ</span>
                            <span
                                className="font-display font-extrabold text-base tabular-nums"
                                style={{ color: tone }}
                            >
                                {score}
                            </span>
                            <span className="text-[11px] font-semibold text-[#0A1F44]/75 truncate">· {verdict}</span>
                        </button>
                        <span className="text-[10px] text-[#1E6BFF] font-semibold shrink-0">Why?</span>
                    </div>
                </div>

                {/* Full dashboard row — hidden on mobile once compact.
                    Always visible on desktop. */}
                <div
                    className={`transition-opacity duration-150 ${
                        compact ? "hidden md:block md:opacity-100" : "block opacity-100"
                    }`}
                >
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-3 flex items-center gap-3 sm:gap-4 md:gap-6">
                        {/* Score block */}
                        <div className="flex items-center gap-3 shrink-0">
                            <button
                                type="button"
                                onClick={() => setActiveSection("affordability")}
                                data-testid="dashboard-score-ring"
                                aria-label={`HomeIQ score ${score}. ${verdict}`}
                                className="relative w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                                style={{
                                    background: `conic-gradient(${tone} ${score * 3.6}deg, #E5E9F2 0deg)`,
                                }}
                            >
                                <div className="absolute inset-1 rounded-full bg-white flex items-center justify-center">
                                    <span className="font-display font-extrabold text-sm text-[#0A1F44] tabular-nums">
                                        {score}
                                    </span>
                                </div>
                            </button>
                            <div className="flex flex-col">
                                <div
                                    data-testid="dashboard-score-label"
                                    className="text-[9px] tracking-[0.25em] uppercase text-[#0A1F44]/50 font-semibold"
                                >
                                    HomeIQ score
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <div className="text-xs font-semibold text-[#0A1F44]">
                                        {verdict}
                                    </div>
                                    <button
                                        type="button"
                                        data-testid="dashboard-score-breakdown"
                                        onClick={() => setWhyOpen(true)}
                                        className="text-[10px] font-semibold text-[#1E6BFF] hover:text-[#0A1F44] underline-offset-2 hover:underline transition-colors"
                                    >
                                        Why?
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Pill row — on mobile each pill is a compact stack
                            (icon · score · short label) so all five modules
                            fit in one row inside a 360px viewport. Desktop
                            keeps the original horizontal label+score pill. */}
                        <div
                            data-testid="dashboard-pill-row"
                            className="flex-1 min-w-0 flex items-stretch justify-between md:justify-start gap-1.5 md:gap-2 md:overflow-x-auto"
                        >
                            {pills.map((p) => {
                                const active = activeSection === p.id;
                                const valueColor = scoreTone(p.value);
                                const Icon = p.Icon;
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => setActiveSection(p.id)}
                                        data-testid={`dashboard-pill-${p.id}`}
                                        aria-label={`${p.label} · ${p.value === "—" ? "not scored" : `score ${p.value}`}`}
                                        className={`shrink md:shrink-0 basis-0 md:basis-auto grow md:grow-0 flex md:hidden flex-col items-center justify-center gap-0.5 rounded-2xl border py-1.5 px-1 min-w-0 transition-colors ${
                                            active
                                                ? "bg-[#0A1F44] text-white border-[#0A1F44]"
                                                : "bg-white text-[#0A1F44] border-[#0A1F44]/12 hover:border-[#1E6BFF]/40"
                                        }`}
                                    >
                                        <Icon
                                            aria-hidden
                                            className={`w-3.5 h-3.5 ${active ? "text-white/85" : "text-[#0A1F44]/70"}`}
                                            strokeWidth={2}
                                        />
                                        <span
                                            className="font-display font-extrabold text-[13px] leading-none tabular-nums"
                                            style={{ color: active ? "#7FA8FF" : valueColor }}
                                        >
                                            {p.value}
                                        </span>
                                        <span className={`text-[9px] font-semibold tracking-wide ${active ? "text-white/75" : "text-[#0A1F44]/60"}`}>
                                            {p.short}
                                        </span>
                                    </button>
                                );
                            })}
                            {/* Desktop pill variant — retains the existing
                                inline label + score treatment. */}
                            {pills.map((p) => {
                                const active = activeSection === p.id;
                                const valueColor = scoreTone(p.value);
                                return (
                                    <button
                                        key={`${p.id}-md`}
                                        type="button"
                                        onClick={() => setActiveSection(p.id)}
                                        data-testid={`dashboard-pill-${p.id}-md`}
                                        className={`hidden md:inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors shrink-0 ${
                                            active
                                                ? "bg-[#0A1F44] text-white border-[#0A1F44]"
                                                : "bg-white text-[#0A1F44] border-[#0A1F44]/15 hover:border-[#1E6BFF]/50"
                                        }`}
                                    >
                                        {p.label}
                                        <span
                                            className="ml-1.5 tabular-nums"
                                            style={{ color: active ? "#7FA8FF" : valueColor }}
                                        >
                                            {p.value}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Generate Report CTA — desktop inline entry. Mobile
                            uses the fixed bottom action bar below. */}
                        <button
                            type="button"
                            onClick={openReport}
                            data-testid="dashboard-generate-report"
                            className="hidden md:inline-flex shrink-0 items-center gap-1.5 px-4 py-2 rounded-full bg-[#0A1F44] hover:bg-[#1E6BFF] text-white text-xs font-semibold transition-colors"
                        >
                            Generate Report
                            <span aria-hidden>→</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile-only fixed bottom action bar. Respects notched safe
                area on iOS; hidden on desktop where the inline CTA
                already covers the same action. */}
            <div
                data-testid="mobile-report-bar"
                className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[#0A1F44]/10 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-1px_0_rgba(10,31,68,0.04)]"
            >
                <button
                    type="button"
                    onClick={openReport}
                    data-testid="mobile-generate-report"
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#0A1F44] hover:bg-[#1E6BFF] text-white text-sm font-semibold transition-colors"
                >
                    Generate Report
                    <span aria-hidden>→</span>
                </button>
            </div>

            <WhyDrawer open={whyOpen} onClose={() => setWhyOpen(false)} />
            <GenerateReportModal open={reportOpen} onClose={() => setReportOpen(false)} />
        </>
    );
}
